import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { StatementsFacade } from '@app/modules/statements/services/statements.facade';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { dedupeArray } from '@app/shared/utils';
import { toPlatformLabel } from '@app/shared/utils/lib/general.util';
import { Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import {
  Account,
  AccountLabels,
  AccountRecognitionPresets,
  AgreementData,
  PayoutParties,
  Platform,
  Property,
  Recognition,
  StandardAccount,
  UICommissionAccountSelection,
  UISplitsSelection,
} from '../../models/statements.models';

const DEFAULT_PERCENTAGE = 25;

const mode = (items: number[]) => {
  const counts = items.reduce((acc, value) => {
    acc[value] = acc[value] ? acc[value] + 1 : 1;
    return acc;
  }, {});
  return Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b), 0);
};
const CUSTOM_ACCOUNTS = Object.values(Account);
const STANDARD_ACCOUNTS = Object.values(StandardAccount);
const PLATFORMS = Object.values(Platform);
const CUSTOM_ACCOUNTS_LENGTH = CUSTOM_ACCOUNTS.length;

@Component({
  standalone: false,
  selector: 'hospitable-statements-commissions-form',
  templateUrl: './statements-commissions-form.component.html',
  styleUrls: ['./statements-commissions-form.component.scss'],
})
export class StatementsCommissionsFormComponent implements OnDestroy, OnInit, OnChanges {
  @Input() agreement: AgreementData;
  @Input() showProperties = true;
  @Input() readonly = false;
  @Output() agreementChange = new EventEmitter<AgreementData>();
  @Output() formValid = new EventEmitter<boolean>();

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  // Properties section
  public propertiesState$ = this.statementsService.propertiesState$;
  public propertiesWithCommission$: Observable<{ id: number; reason: string }[]> = of([]);

  // Main form
  public form = this.fb.group({
    // These fields *mostly* correspond to the API fields, but are flattened/reorganized for easier form manipulation
    // See the methods formValuesToApi and apiToFormValues for implementation
    name: this.fb.control(`Agreement ${new Date().toLocaleDateString()}`, [Validators.required]),
    type: this.fb.control(PayoutParties.Host, [Validators.required]),
    commission_rates: this.fb.group(
      this.reduceToFormGroup(PLATFORMS, DEFAULT_PERCENTAGE, [Validators.max(100), Validators.min(0)])
    ),
    // TODO: validate that custom selection has at least one more value
    commission_accounts: this.fb.control([], []),
    commission_recognition: this.fb.control(Recognition.ProRated, [Validators.required]),
    splits: this.fb.group(this.reduceToFormGroup(CUSTOM_ACCOUNTS, 100, [Validators.max(100), Validators.min(0)])),
    recognition: this.fb.group(this.reduceToFormGroupForRecognitionWithPresets(CUSTOM_ACCOUNTS)),
    flat_fees: this.fb.group(this.reduceToFormGroup(PLATFORMS, 0, [Validators.min(0)])),
    taxes: this.fb.control(PayoutParties.Host, [Validators.required]),
    properties: this.fb.control([], []),
    // UI fields represent the nice toggles that the user sees, which are aggregated from the API fields
    // and not propogated back to the server
    ui: this.fb.group({
      flatFeesShow: this.fb.control(false, []),
      flatFeesPerPlatform: this.fb.control(false, []),
      flatFeesDefault: this.fb.control(0, [Validators.min(0)]),
      commissionRatesPerPlatform: this.fb.control(false, []),
      commissionRatesDefault: this.fb.control(DEFAULT_PERCENTAGE, [Validators.max(100), Validators.min(0)]),
      commissionAccounts: this.fb.control(UICommissionAccountSelection.Accommodation, []),
      splits: this.fb.control(UISplitsSelection.Host, []),
      recognition: this.fb.control(Recognition.ProRated, []),
    }),
  });
  public payoutsOptions = this.statementsService.payoutsOptions;
  public perPlatformOptions = this.setPlatformOptions();
  public commissionAccountOptions = this.statementsService.commissionAccountOptions;
  public commissionAccountCustomOptions = CUSTOM_ACCOUNTS.map((account) => ({
    value: account,
    label: AccountLabels[account],
  }));
  public splitOptions = this.statementsService.splitOptions;
  public taxOptions = this.statementsService.taxOptions;
  public recognitionOptions = this.statementsService.recognitionOptions;
  public splitRecognitionOptions = this.statementsService.splitRecognitionOptions;

  constructor(
    private statementsService: StatementsFacade,
    private fb: UntypedFormBuilder,
    private optimizely: OptimizelyService
  ) {
    this.statementsService.getProperties();

    //pipe property state and update propertiesWithCommission as a result - any properties in state.data which have a commission set
    this.propertiesWithCommission$ = this.statementsService.propertiesState$.pipe(
      map((state) => state.data.filter((property) => property.commissionRates?.length)),
      map((properties) => {
        return properties.map((property) => ({
          id: property.id,
          reason: `This property already has a commission rate`,
        }));
      })
    );
  }

  public ngOnInit() {
    this.listenForChanges();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.agreement && changes.agreement.currentValue && !changes.agreement.previousValue) {
      this.form.patchValue(this.apiToFormValues(changes.agreement.currentValue));
    }

    if (
      changes.readonly &&
      changes.readonly.currentValue !== changes.readonly.previousValue &&
      changes.readonly.currentValue
    ) {
      this.form.disable();
    }
  }

  public ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public setProperties(properties: Property[]) {
    this.form.get('properties').setValue(properties.map((property) => property.id));
  }

  public compareCommissionAccountSelection(a: string, b: string) {
    return a === b;
  }

  /**
   * Getters for the form elements
   */
  public get splitsGroup() {
    return this.form.get('splits');
  }

  public get recognitionGroup() {
    return this.form.get('recognition');
  }

  public get uiSplitsCtrl() {
    return this.form.get('ui.splits');
  }

  public uiCtrl(fieldName: string) {
    return this.form.get(`ui.${fieldName}`);
  }

  /**
   * Listeners for form value changes that affect other form controls
   */
  private listenForChanges() {
    this.emitFormStatus();
    this.emitFormValues();
    this.setFlatFeesUsingDefault();
    this.setCommissionRatesUsingDefault();
    this.setCommissionAccountsFromDefaults();
    this.setSplitsFromDefaults();
  }

  private setSplitsFromDefaults() {
    this.form
      .get('commission_accounts')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.splitsGroup.enable();
        if (!value.length || this.isPayout(value)) {
          CUSTOM_ACCOUNTS.forEach((account) => {
            this.form.get(`splits.${account}`)?.enable();
            // this.form.get(`splits.${account}`)?.setValue(100);
            this.form.get(`recognition.${account}`)?.enable();
            this.form.get(`recognition.${account}`)?.setValue(AccountRecognitionPresets[account]);
          });
          return;
        }

        const patch = value.reduce((acc, source) => {
          return { ...acc, [source]: null };
        }, {});
        this.splitsGroup.patchValue(patch);
        this.recognitionGroup.patchValue(patch);
        value.forEach((account) => {
          this.form.get(`splits.${account}`)?.disable();
          this.form.get(`recognition.${account}`)?.disable();
        });
      });

    this.uiCtrl('splits')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        CUSTOM_ACCOUNTS.forEach((account) => {
          const validators = value === UISplitsSelection.Custom ? [Validators.required] : [];
          this.form.get(`splits.${account}`)?.setValidators(validators);
        });
      });
  }

  private setCommissionAccountsFromDefaults() {
    this.uiCtrl('commissionAccounts')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (this.form.disabled) {
          return;
        }

        if (value === UICommissionAccountSelection.Custom) {
          this.uiSplitsCtrl.setValue(UISplitsSelection.Custom);
          this.uiSplitsCtrl.disable();

          return;
        }

        if (value === UICommissionAccountSelection.Payout) {
          this.uiSplitsCtrl.setValue(null);
          this.uiSplitsCtrl.disable();
          return;
        }

        this.form.get('commission_accounts').setValue([]);
        this.uiSplitsCtrl.enable();
        if (!this.uiSplitsCtrl.value) {
          this.uiSplitsCtrl.setValue(UISplitsSelection.Host);
        }
      });
  }

  private setCommissionRatesUsingDefault() {
    this.uiCtrl('commissionRatesPerPlatform')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.syncPerPlatformToDefault(value, 'commission_rates', 'commissionRatesDefault');
      });

    this.uiCtrl('commissionRatesDefault')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.syncDefaultToPerPlatform(value, 'commission_rates', 'ui.commissionRatesPerPlatform');
      });
  }

  private setFlatFeesUsingDefault() {
    this.uiCtrl('flatFeesPerPlatform')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.syncPerPlatformToDefault(value, 'flat_fees', 'flatFeesDefault');
      });

    this.uiCtrl('flatFeesDefault')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.syncDefaultToPerPlatform(value, 'flat_fees', 'ui.flatFeesPerPlatform');
      });
  }

  // when the default value changes, update all the sub fields
  private syncDefaultToPerPlatform(value, fieldName, checkboxFieldName) {
    if (this.form.get(checkboxFieldName).value) {
      return;
    }

    const patch = this.perPlatformOptions.reduce((acc, option) => {
      return { ...acc, [`${option.value}`]: value };
    }, {});
    this.form.patchValue({ [fieldName]: { ...patch } });
  }

  // The subfields are what actually get submitted to the API, and the default is just
  // a way to set all the subfields to the same value at once
  private syncPerPlatformToDefault(value, fieldName, defaultFieldName) {
    if (value) {
      return;
    }

    // when unselected, get the most common subvalue and set it as default
    const values = this.perPlatformOptions.map((option) => this.form.get(`${fieldName}.${option.value}`).value);
    const mostCommon = mode(values);
    // patch the default value and all the sub fields
    const patch = this.perPlatformOptions.reduce((acc, option) => {
      return { ...acc, [`${fieldName}.${option.value}`]: mostCommon };
    }, {});
    this.form.patchValue({ ...patch, ui: { [defaultFieldName]: mostCommon } });
  }

  private emitFormValues() {
    this.form.valueChanges.pipe(startWith(this.form.value), takeUntil(this.destroyed$)).subscribe((values) => {
      const vals = this.formValuesToApi(values);
      this.agreementChange.emit(vals);
    });
  }

  private emitFormStatus() {
    this.form.statusChanges
      .pipe(
        map(() => this.form.valid),
        takeUntil(this.destroyed$)
      )
      .subscribe((valid) => {
        this.formValid.emit(valid);
      });
  }

  private formValuesToApi(values): AgreementData {
    const {
      ui = {},
      properties,
      commission_rates = [],
      commission_accounts = [],
      commission_recognition = Recognition.ProRated,
      splits = {},
      flat_fees = [],
      recognition = {},
      ...apiValues
    } = values;

    return {
      ...apiValues,
      commission: {
        rates: this.groupToRates(commission_rates),
        accounts: dedupeArray(this.commissionAccountsUItoAPI(ui?.commissionAccounts, commission_accounts)),
        recognition: commission_recognition,
      },
      splits: this.splitsUItoAPI({
        commissionAccountsUiSelection: ui?.commissionAccounts,
        uiSelection: ui?.splits,
        values: splits,
        recognitionUiSelection: ui?.recognition,
        recognitionValues: recognition,
      }),
      flat_fees: this.groupToRates(flat_fees, !ui.flatFeesShow),
      properties: properties?.map?.((id) => ({ id })),
    };
  }

  private splitsUItoAPI({
    commissionAccountsUiSelection,
    uiSelection,
    values,
    recognitionUiSelection,
    recognitionValues,
  }) {
    if (commissionAccountsUiSelection === UICommissionAccountSelection.Payout) {
      return {};
    }

    const preselectedRate = uiSelection === UISplitsSelection.Owner ? 0 : 100;
    // The parent field can be disabled, in which case its value gets stripped from the form values
    // It can be disabled when the only selection must be custom
    const isCustomOrDisabled = !uiSelection || uiSelection === UISplitsSelection.Custom;
    const recognitionIsCustomOrDisabled = !recognitionUiSelection || recognitionUiSelection === 'custom';

    const splits = Object.entries(values)
      .filter(([key, val]) => (isCustomOrDisabled ? val !== null : true))
      .map(([account, rate]) => {
        return {
          account,
          rate: isCustomOrDisabled ? (rate ?? 0) : preselectedRate,
          recognition: recognitionIsCustomOrDisabled ? recognitionValues[account] : recognitionUiSelection,
        };
      });

    return splits;
  }

  private commissionAccountsUItoAPI(uiSelection, value) {
    if (uiSelection === UICommissionAccountSelection.Accommodation) {
      return [...STANDARD_ACCOUNTS];
    }
    if (uiSelection === UICommissionAccountSelection.Payout) {
      return [...STANDARD_ACCOUNTS, ...CUSTOM_ACCOUNTS];
    }
    return dedupeArray([...value, ...STANDARD_ACCOUNTS]);
  }

  private apiToFormValues(apiValues: AgreementData) {
    apiValues = this.patchMissingFields(apiValues);
    return {
      type: apiValues.type,
      name: apiValues.name,
      commission_rates: this.ratesToGroup(apiValues.commission.rates),
      commission_accounts: apiValues.commission.accounts,
      commission_recognition: apiValues.commission.recognition,
      splits: this.ratesToGroup(apiValues.splits),
      recognition: this.splitsToRecognitionGroup(apiValues.splits),
      flat_fees: this.ratesToGroup(apiValues.flat_fees),
      taxes: apiValues.taxes,
      properties: apiValues.properties,
      ui: {
        flatFeesShow: apiValues.flat_fees.some((fee) => fee.rate > 0),
        flatFeesPerPlatform: !apiValues.flat_fees.every((fee) => fee.rate === apiValues.flat_fees[0].rate),
        flatFeesDefault: mode(apiValues.flat_fees.map((fee) => fee.rate)),
        commissionRatesPerPlatform: !apiValues.commission.rates.every(
          (rate) => rate.rate === apiValues.commission.rates[0].rate
        ),
        commissionRatesDefault: mode(apiValues.commission.rates.map((rate) => rate.rate)),
        commissionAccounts: this.commissionAccountsUISelected(apiValues.commission.accounts),
        splits: this.splitsUISelected(apiValues.splits),
        recognition: this.recognitionUISelected(apiValues.splits),
      },
    };
  }

  // If a value of the field was set to zero, the API will not include that field in the GET response.
  // To keep the form data consistent, we need to add those fields back in with a value of zero.
  private patchMissingFields(vals: AgreementData) {
    const { commission, splits, flat_fees, ...rest } = vals;
    const fillRates = (templateVals, data, keyName) => {
      return templateVals.map((template) => {
        const value = data.find((val) => val[keyName] === template);
        return value || { [keyName]: template, rate: 0 };
      });
    };

    return {
      ...rest,
      commission: {
        ...commission,
        rates: fillRates(PLATFORMS, commission.rates, 'scope'),
      },
      splits: fillRates(
        CUSTOM_ACCOUNTS.filter((account) => !commission.accounts.includes(account)),
        splits,
        'account'
      ),
      flat_fees: fillRates(PLATFORMS, flat_fees, 'scope'),
    };
  }

  private groupToRates(group, excluded = false) {
    return Object.entries(group).map(([scope, rate]) => ({ scope, rate: excluded ? 0 : rate }));
  }

  private ratesToGroup(
    rates: Array<{ scope?: Platform; rate: number; account?: Account | StandardAccount; recognition?: Recognition }>
  ) {
    return rates.reduce((acc, rate) => {
      const field = rate.scope || rate.account || rate.recognition;
      return { ...acc, [field]: rate.rate };
    }, {});
  }

  private splitsToRecognitionGroup(rates: Array<{ account?: Account | StandardAccount; recognition?: Recognition }>) {
    return rates.reduce((acc, rate) => {
      return { ...acc, [rate.account]: rate.recognition };
    }, {});
  }

  private isPayout(values: string[]) {
    return values.length === CUSTOM_ACCOUNTS_LENGTH && CUSTOM_ACCOUNTS.every((account) => values.includes(account));
  }

  private commissionAccountsUISelected(values) {
    if (values.length === 2 && STANDARD_ACCOUNTS.every((account) => values.includes(account))) {
      return UICommissionAccountSelection.Accommodation;
    }

    if (this.isPayout(values)) {
      return UICommissionAccountSelection.Payout;
    }

    return UICommissionAccountSelection.Custom;
  }

  private splitsUISelected(values) {
    const isStandardSplit = values.length === CUSTOM_ACCOUNTS.length;
    if (isStandardSplit && values.every((split) => split.rate === 100)) {
      return UISplitsSelection.Host;
    }

    if (isStandardSplit && values.every((split) => split.rate === 0)) {
      return UISplitsSelection.Owner;
    }

    return UISplitsSelection.Custom;
  }

  private recognitionUISelected(values) {
    if (values.every((split) => split.recognition === Recognition.ProRated)) {
      return Recognition.ProRated;
    }

    if (values.every((split) => split.recognition === Recognition.CheckIn)) {
      return Recognition.CheckIn;
    }

    if (values.every((split) => split.recognition === Recognition.CheckOut)) {
      return Recognition.CheckOut;
    }

    return 'custom';
  }

  private reduceToFormGroup(enumValues: string[], defaultValue, controlOpts = []) {
    return enumValues.reduce((acc, name) => {
      return { ...acc, [name]: this.fb.control(defaultValue, controlOpts) };
    }, {});
  }

  private reduceToFormGroupForRecognitionWithPresets(enumValues: string[]) {
    return enumValues.reduce((acc, name) => {
      return { ...acc, [name]: this.fb.control(AccountRecognitionPresets[name], [Validators.required]) };
    }, {});
  }

  private setPlatformOptions() {
    return PLATFORMS.map((platform) => ({
      value: platform,
      label: toPlatformLabel(platform),
    }));
  }
}
